import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { authApi } from "./auth";
import { store } from "../store";
import { logout, setCredentials } from "../features/authSlice";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

const getAuthToken = () => {
  return localStorage.getItem("refreshToken");
};

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      const refresh = getAuthToken();

      if (refresh) {
        try {
          const refreshResult = await api.dispatch(
            authApi.endpoints.refreshToken.initiate({ refreshToken: refresh })
          );

          if (refreshResult.data.respuestaToken !== null) {
            const newToken = refreshResult.data.respuestaToken.accessToken;
            const newRefreshToken = refreshResult.data.respuestaToken.refreshToken;

            const userData = {
              respuestaToken: {
                accessToken: newToken,
                refreshToken: newRefreshToken,
              },
            };
            store.dispatch(setCredentials(userData));
            processQueue(null, newToken);

            result = await baseQuery(
              {
                ...args,
                headers: {
                  ...args.headers,
                  Authorization: `Bearer ${newToken}`,
                },
              },
              api,
              extraOptions
            );
          } else {
            processQueue("Token refresh failed", null);
            store.dispatch(logout());
            toast.error(
              "Lo sentimos, tu sesión ha expirado debido a inactividad. Por favor, vuelve a iniciar sesión para continuar.",
              {
                position: toast.POSITION.TOP_RIGHT,
              }
            );
          }
        } catch (error) {
          processQueue(error, null);
          store.dispatch(logout());
          toast.error(
            "Error al intentar refrescar el token. Por favor, vuelve a iniciar sesión.",
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
        } finally {
          isRefreshing = false;
        }
      } else {
        store.dispatch(logout());
        toast.error(
          "No se encontró un token de refresco. Por favor, vuelve a iniciar sesión.",
          {
            position: toast.POSITION.TOP_RIGHT,
          }
        );
      }
    } else {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
      .then(token => {
        result = baseQuery(
          {
            ...args,
            headers: {
              ...args.headers,
              Authorization: `Bearer ${token}`,
            },
          },
          api,
          extraOptions
        );
        return result;
      })
      .catch(err => {
        return result;
      });
    }
  }

  return result;
};

export const modalidadApi = createApi({
  reducerPath: "modalidadApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Modalidades"],
  endpoints: (builder) => ({
    // getListaModalidades: builder.query({
    //     query: () => '/buscar-lista-modalidades',
    //     providesTags: ['Modalidades'],
    // }),
    getListaModalidadesSelect: builder.query({
      query: () => "/listar-modalidades",
      providesTags: ["Modalidades"],
    }),
    agregarModalidad: builder.mutation({
      query: (nuevaModalidad) => {
        const formData = new FormData();
        Object.keys(nuevaModalidad).forEach((key) => {
          formData.append(String(key), nuevaModalidad[key]);
        });
        return {
          url: `/agregar-modalidad`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Modalidades"],
    }),
    modificarModalidad: builder.mutation({
      query: (nuevaModalidad) => {
        const formData = new FormData();
        Object.keys(nuevaModalidad).forEach((key) => {
          formData.append(String(key), nuevaModalidad[key]);
        });
        return {
          url: `/modificar-modalidad`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Modalidades"],
    }),
    borrarModalidad: builder.mutation({
      query: (id) => ({
        url: `/borrar-modalidad?Id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Modalidades"],
    }),
    getModalidadById: builder.query({
      query: (id) => `buscar-modalidad-individual?id=${id}`,
    }),
    // Invalidar la consulta después de agregar una modalidad
    onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
      await queryFulfilled;
      dispatch(modalidadApi.endpoints.getListaModalidades.query());
    },
  }),
});

export const {
  useGetModalidadByIdQuery,
  // useGetListaModalidadesQuery,
  useGetListaModalidadesSelectQuery,
  useAgregarModalidadMutation,
  useModificarModalidadMutation,
  useBorrarModalidadMutation,
} = modalidadApi;
